import React, { useMemo, useState, useEffect } from "react";
import { Card, Col, Form, Input, InputNumber, Row, Tree, Checkbox } from "antd";
import { PlusSquareOutlined, MinusSquareOutlined } from "@ant-design/icons";
import type { DataNode } from "antd/es/tree";
import type { AdminConsoleMeta } from "../../../types";
import { toLabel } from "../utils";
import { groupedRolePermissions } from "../constants";

interface BackendRoleFieldsProps {
  meta: AdminConsoleMeta | null;
}

export const BackendRoleFields: React.FC<BackendRoleFieldsProps> = ({ meta }) => {
  const form = Form.useFormInstance();
  const allAvailableFields = useMemo(() => meta?.backendRoleFields || [], [meta]);

  const validPermissionValues = useMemo(() => {
    const values = new Set<string>();
    groupedRolePermissions?.forEach((g) => {
      g.subgroups?.forEach((sg) => {
        sg.rolePermissions?.forEach((p) => values.add(p.value));
      });
    });
    allAvailableFields.forEach((f) => values.add(f));
    return values;
  }, [allAvailableFields]);

  const { treeData } = useMemo(() => {
    const groupedValuesInTree = new Set<string>();

    const mainNodes: DataNode[] = groupedRolePermissions.map((group) => ({
      title: group.groupName,
      key: `group:${group.groupName}`,
      children: group.subgroups.map((subgroup) => ({
        title: subgroup.subgroupName,
        key: `subgroup:${group.groupName}:${subgroup.subgroupName}`,
        children: subgroup.rolePermissions.map((p) => {
          groupedValuesInTree.add(p.value);
          return {
            title: p.label,
            key: p.value,
            disabled: !allAvailableFields.includes(p.value),
          };
        }),
      })),
    }));

    const otherFields = allAvailableFields.filter((f) => !groupedValuesInTree.has(f));
    const otherNodes = otherFields.map((f) => ({
      title: toLabel(f),
      key: f,
    }));

    if (otherNodes.length > 0) {
      mainNodes.push({
        title: "Other Permissions",
        key: "group:Other",
        children: otherNodes,
      });
    }

    return { treeData: mainNodes, uncategorizedLeaves: otherFields };
  }, [allAvailableFields]);

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);

  const allValues = Form.useWatch([], form);

  useEffect(() => {
    if (!allValues) return;
    const checked = Array.from(validPermissionValues).filter((v) => allValues[v] === true);
    setCheckedKeys(checked);
  }, [allValues, validPermissionValues]);

  const onCheck = (checked: any) => {
    const checkedArray = Array.isArray(checked) ? checked : checked.checked;
    
    const updates: Record<string, boolean> = {};
    
    validPermissionValues.forEach((v) => {
      updates[v] = checkedArray.includes(v);
    });

    form.setFieldsValue(updates);
    setCheckedKeys(checkedArray);
  };

  return (
    <Row gutter={[16, 24]}>
      <Col span={12}>
        <Form.Item label="Role Name" name="name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Administrator" />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item label="Level (1-10)" name="level">
          <InputNumber min={1} max={10} style={{ width: "100%" }} />
        </Form.Item>
      </Col>

      <Col span={24}>
        <Card size="small" title="Permissions Matrix" className="admin-console__permission-card">
          <div className="admin-console__tree-wrapper">
            <Tree
              checkable
              selectable={false}
              treeData={treeData}
              checkedKeys={checkedKeys}
              onCheck={onCheck}
              className="admin-console__permissions-tree"
              showLine={{ showLeafIcon: false }}
              switcherIcon={(nodeProps: any) => {
                if (nodeProps.isLeaf) return null;
                return nodeProps.expanded ? (
                  <MinusSquareOutlined style={{ fontSize: "14px", color: "#64748b" }} />
                ) : (
                  <PlusSquareOutlined style={{ fontSize: "14px", color: "#64748b" }} />
                );
              }}
            />
          </div>
          <div style={{ display: "none" }}>
            {Array.from(validPermissionValues).map((v) => (
              <Form.Item key={v} name={v} valuePropName="checked">
                <Checkbox />
              </Form.Item>
            ))}
          </div>
        </Card>
      </Col>
    </Row>
  );
};
